import React from 'react';
import { useEditorStore } from '@/core/stores/editor-store';
import { useStageStore } from '@/core/stores/stage-store';
import { zoomToPercent } from '@/canvas/coordinates';

interface StatusBarProps {
  cursorWorldX?: number;
  cursorWorldY?: number;
}

const statusBarToolNames: Record<string, string> = {
  select: 'Select',
  place: 'Place',
  move: 'Move',
  rotate: 'Rotate',
  scale: 'Scale',
  wall_draw: 'Draw Wall',
  measure: 'Measure',
  delete: 'Delete',
};

const StatusBar: React.FC<StatusBarProps> = ({
  cursorWorldX = 0,
  cursorWorldY = 0,
}) => {
  const { activeTool, selectedObjectIds, zoom, isDirty } = useEditorStore();
  const { layout } = useStageStore();

  const toolName = statusBarToolNames[activeTool] || activeTool;
  const objectCount = layout?.objects.length || 0;
  const selectedCount = selectedObjectIds.length;

  return (
    <div className="h-8 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex items-center px-3 gap-4 text-xs text-gray-600 dark:text-gray-400 shadow-sm">
      {/* Dirty indicator */}
      {isDirty && (
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-orange-500 rounded-full" />
          <span>Unsaved</span>
        </div>
      )}

      {/* Current tool */}
      <div className="flex items-center gap-2">
        <span className="text-gray-500 dark:text-gray-500">Tool:</span>
        <span className="font-medium text-gray-900 dark:text-gray-100">
          {toolName}
        </span>
      </div>

      {/* Separator */}
      <div className="h-4 w-px bg-gray-300 dark:bg-gray-600" />

      {/* Cursor position */}
      <div className="flex items-center gap-2 font-mono">
        <span className="text-gray-500 dark:text-gray-500">Cursor:</span>
        <span className="text-gray-900 dark:text-gray-100">
          X: {cursorWorldX.toFixed(2)}m
        </span>
        <span className="text-gray-900 dark:text-gray-100">
          Y: {cursorWorldY.toFixed(2)}m
        </span>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Object count */}
      <div className="flex items-center gap-2">
        <span className="text-gray-500 dark:text-gray-500">Objects:</span>
        <span className="font-mono font-medium text-gray-900 dark:text-gray-100">
          {objectCount}
        </span>
        {selectedCount > 0 && (
          <>
            <span className="text-gray-500 dark:text-gray-500">/</span>
            <span className="font-mono font-medium text-blue-600 dark:text-blue-400">
              {selectedCount} selected
            </span>
          </>
        )}
      </div>

      {/* Separator */}
      <div className="h-4 w-px bg-gray-300 dark:bg-gray-600" />

      {/* Stage size */}
      {layout?.canvas && (
        <>
          <div className="flex items-center gap-2 font-mono">
            <span className="text-gray-500 dark:text-gray-500">Stage:</span>
            <span className="text-gray-900 dark:text-gray-100">
              {layout.canvas.width_m}m × {layout.canvas.height_m}m
            </span>
          </div>
          <div className="h-4 w-px bg-gray-300 dark:bg-gray-600" />
        </>
      )}

      {/* Zoom percentage */}
      <div className="flex items-center gap-2 font-mono">
        <span className="text-gray-500 dark:text-gray-500">Zoom:</span>
        <span className="text-gray-900 dark:text-gray-100">
          {zoomToPercent(zoom)}%
        </span>
      </div>
    </div>
  );
};

export default StatusBar;
